import React, { Suspense, useCallback, useEffect, useState } from "react";
import { Loader } from "./controls";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import "./styles/App.scss";
import "./styles/Style.scss";
import "./styles/rtl.scss";
import SiteRoute from "./pages/SiteRoute";
import ServerError from "./pages/ServerError";
import { Login, Forgot } from "./components/auth";
import { API, action, controller } from "./api/api";
import { lsSave, ToFormData } from "./hooks/Method";
import Toast from "layout/Toast";
import { CACHE_VERSION } from "config/version";
import { AppContext, RegisterProvider } from "./context";
import ThemeLocaleProvider from "./i18n/ThemeLocaleProvider";
import { setStoreTimeZone } from "./utils/dayjs";
import { installNumericInputGuard } from "./utils/numericInput";
import i18n from "./i18n/i18n";

const App = () => {
  const [loader, setLoader] = useState(false);
  const [authUser, setAuthUser] = useState();
  const [bizContext, setBizContext] = useState({
    orgId: authUser?.orgId || 0,
    companyId: "",
    outletId: "",
  });
  // Re-opens the company/branch prompt on demand. Before this the only way to change
  // branch was to clear localStorage, which put warehouse and back-office staff — who
  // never open a till and so have no register outlet either — in a dead end.
  const [bizPrompt, setBizPrompt] = useState(false);
  const openBizContext = useCallback(() => setBizPrompt(true), []);

  const [selCompany, setSelCompany] = useState(null);

  const [settings, setSettings] = useState({});
  const [access, setAccess] = useState({});
  const [org, setOrg] = useState({});
  const [serverError, setServerError] = useState(null);

  // What a number field will accept, decided once for the whole app rather than
  // remembered at each of the ~150 call sites. See utils/numericInput.js.
  useEffect(() => installNumericInputGuard(), []);

  // Listen for server-error events from axios interceptor
  useEffect(() => {
    const handleServerError = (e) => setServerError(e.detail?.type || "network");
    window.addEventListener("server-error", handleServerError);
    return () => window.removeEventListener("server-error", handleServerError);
  }, []);

  // ----- Clear browser local storage data base on the version changes -----
  useEffect(() => {
    const savedVersion = localStorage.getItem("cache_version");
    if (savedVersion !== CACHE_VERSION) {
      console.log("Cache version changed, clearing localStorage");
      localStorage.clear();
      localStorage.setItem("cache_version", CACHE_VERSION);
    }
  }, []);

  // The branch NAME, published on bizContext alongside its id.
  //
  // bizContext has only ever carried ids, so every screen that wanted to say "you
  // are working in Salmiya" had to fetch it again. The stock-transfer screens ask
  // "which branch am I?" constantly, so the label is resolved once here and merged
  // in. Guarded on the label being absent, so this fires once per branch change and
  // not on every render — and it re-fires after a switch because outletId changes.
  useEffect(() => {
    if (!bizContext?.companyId || !bizContext?.outletId) return;
    if (bizContext?.outletLabel) return;

    API(controller.Company + action.BizContextDetails).post(
      ToFormData({ ...bizContext }),
      (res) => {
        if (res?.status !== 200) return;
        setBizContext((prev) => {
          const next = { ...prev, companyLabel: res.result?.companyLabel, outletLabel: res.result?.outletLabel };
          lsSave("bizContext", next);
          return next;
        });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizContext?.companyId, bizContext?.outletId, bizContext?.outletLabel]);

  // Publish the company's business timezone app-wide so every stored-UTC timestamp

  // (receipts, sale history, returns, quotes, reports) renders in the store zone
  // instead of the viewing device's zone. Empty → falls back to device-local.
  useEffect(() => {
    setStoreTimeZone(settings?.timeZone);
  }, [settings?.timeZone]);

  // Fetch auth user
  useEffect(() => {
    const fetchAuthUser = async () => {
      try {
        await API(controller.Auth + action.GetAuthUser).get((res, err) => {
          if (res && res.status === 200 && res.result) {
            if (res.result.defaultLangCode) {
              i18n.changeLanguage(res.result.defaultLangCode);
            }
            setAuthUser(res.result);
            res.result?.org
              ? setOrg({ ...res.result?.org })
              : console.error("Org information not found!");
          } else setAuthUser(null);
        });
      } catch (error) {
        console.error("Auth user fetch error:", error);
        setAuthUser(null);
      }
    };
    fetchAuthUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The register fetch that used to live here has moved into RegisterProvider.
  // Two writers of `localStorage.activeReg` racing each other is the "two versions
  // of the truth" this codebase forbids — and this one was the broken half: it
  // guarded on `res.status === 200`, while the backend answered "no open session"
  // as an Ok()-wrapped body of `{ status: 404 }`, so its clear branch never ran.
  const refreshSettings = () => {
    if (authUser?.id && selCompany) {
      API(controller.Org + action.GetSettings).getById(selCompany, (res) => {
        if (res?.status === 200) {
          // userType (account tier) is returned alongside settings; fold it in so
          // menu/route gating can treat System-Admin as a first-class condition.
          setSettings({ ...res?.settings, userType: res?.userType });
          setAccess({ ...res?.featureAccess });
        }
      });
    }
  };

  useEffect(() => {
    refreshSettings();
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, selCompany]);

  return (
    <Suspense fallback={<Loader show />}>
      <ThemeLocaleProvider
        uiTemplate={settings?.uiTemplate}
        accentTheme={settings?.accentTheme}
      >
        <AppContext.Provider
          value={{
            loader,
            setLoader,
            authUser,
            setAuthUser,
            settings,
            access,
            org,
            setSelCompany,
            selCompany,
            bizContext,
            setBizContext,
            bizPrompt,
            setBizPrompt,
            openBizContext,
            refreshSettings,

          }}
        >
          {/* Inside AppContext (it needs authUser + selCompany) and above the
              Router, so POS, Close Register, Cash Management, the returns screens
              and Stock Wastage all share one server-confirmed answer. */}
          <RegisterProvider>
          <Loader show={loader} />
          {serverError && (
            <ServerError
              type={serverError}
              onRetry={() => {
                setServerError(null);
                window.location.reload();
              }}
              onDismiss={() => setServerError(null)}
            />
          )}
          <Router>
            <Routes>
              <Route
                path="/*"
                element={authUser ? <SiteRoute /> : !loader && <Auth />}
              />
            </Routes>
          </Router>
          <Toast />
          </RegisterProvider>
        </AppContext.Provider>
      </ThemeLocaleProvider>
    </Suspense>
  );
};

const Auth = () => (
  <>
    <Routes>
      <Route path="/" element={<Navigate to="auth" />} />
      <Route path="/auth">
        <Route path="forgot" element={<Forgot />} />
        <Route index element={<Login />} />
      </Route>
    </Routes>
  </>
);

export default App;
